import { message } from 'antd'
import React from 'react'
import styled from 'styled-components/macro'
import { css } from 'styled-components/macro'
import {
  getHarbourVenueDisplayName,
  HARBOUR_COPY_EMAILS_ACTION,
  HARBOUR_COPY_EMAILS_TOAST,
  HARBOUR_NO_PHONE_COPY,
  HARBOUR_WHATSAPP_CHAT_ACTION,
  formatHarbourSisterTalkingPoint,
} from 'src/constants/harbour'
import {
  canHarbourStartWork,
  getHarbourCardAskProductNudge,
  getHarbourCardAskSalesNudge,
  getHarbourCardChefName,
  getHarbourCardCsOwnerLabel,
  getHarbourCardFollowUpStatus,
  getHarbourCardPlanLabel,
} from 'src/utils/harbourCard'
import {
  getHarbourCardLadder,
  getHarbourCardRadar,
  splitHarbourCardLadder,
} from 'src/utils/harbourLastUsed'
import {
  copyHarbourEmails,
  getHarbourVenueEmails,
} from 'src/utils/harbourEmails'
import { getHarbourSisterGroupOtherSites } from 'src/utils/harbourMappers'
import {
  copyHarbourPhone,
  getHarbourPhoneHref,
  getHarbourPhoneNumber,
  getHarbourWhatsAppChatHref,
} from 'src/utils/harbourPhone'
import { HARBOUR_TOOLTIPS } from 'src/utils/harbourTooltips'

/**
 * Compact Harbour card — radar (owner last seen / order), adoption ladder, contact.
 * Buttons are the work loop. Drag is not the primary action.
 */
const HarbourCard = ({
  card,
  now,
  startBusy = false,
  onSelectVenue,
  onStartWork,
  onAskSales,
  onAskProduct,
}) => {
  if (!card?.venueId) {
    return null
  }

  const phone = getHarbourPhoneNumber(card)
  const phoneHref = getHarbourPhoneHref(phone)
  const whatsAppChatHref = getHarbourWhatsAppChatHref(phone)
  const chefName = getHarbourCardChefName(card)
  const venueName = getHarbourVenueDisplayName(card.venueName)
  const csOwnerLabel = getHarbourCardCsOwnerLabel(card)
  const planLabel = getHarbourCardPlanLabel(card)
  const followUpStatus = getHarbourCardFollowUpStatus(card)
  const radar = getHarbourCardRadar(card, now)
  const ladder = getHarbourCardLadder(card, now)
  const { critical: ladderCritical, secondary: ladderSecondary } =
    splitHarbourCardLadder(ladder)
  const askSales = getHarbourCardAskSalesNudge(card)
  const askProduct = getHarbourCardAskProductNudge(card)
  const emails = getHarbourVenueEmails(card)
  const otherSites = getHarbourSisterGroupOtherSites(
    card.sisterGroup,
    card.venueId,
  )

  const handleCopyPhone = event => {
    event.preventDefault()
    event.stopPropagation()

    async function copyPhone() {
      try {
        await copyHarbourPhone(phone)
        message.success('Phone copied')
      } catch (err) {
        message.error(err.message || 'Could not copy phone')
      }
    }

    copyPhone()
  }

  const handleCopyEmails = event => {
    event.preventDefault()
    event.stopPropagation()

    async function copyEmails() {
      try {
        await copyHarbourEmails(card)
        message.success(HARBOUR_COPY_EMAILS_TOAST)
      } catch (err) {
        message.error(err.message || 'Could not copy emails')
      }
    }

    copyEmails()
  }

  const handleAskProduct = event => {
    event.preventDefault()
    event.stopPropagation()
    if (onAskProduct) {
      onAskProduct(event, card)
    }
  }

  const handleAskSales = event => {
    event.preventDefault()
    event.stopPropagation()
    if (onAskSales) {
      onAskSales(event, card)
      return
    }
    onSelectVenue?.(card)
  }

  return (
    <Card
      data-testid={`harbour-card-${card.venueId}`}
      data-venue-id={card.venueId}
      data-column={card.column}
      data-overdue={card.overdue ? 'true' : 'false'}
      data-forgotten={card.forgotten ? 'true' : 'false'}
      data-no-phone={phone ? 'false' : 'true'}
      data-urgency={
        card.overdue ? 'overdue' : card.forgotten ? 'forgotten' : ''
      }
      data-drag-primary='false'
      draggable={false}
      onClick={() => onSelectVenue?.(card)}
    >
      <CardTop>
        <TitleBlock>
          <VenueName title={venueName}>{venueName}</VenueName>
        </TitleBlock>
        <Badges>
          <PlanBadge
            data-plan={card.paid ? 'paid' : 'free'}
            data-testid={`harbour-card-plan-${card.venueId}`}
          >
            {planLabel}
          </PlanBadge>
          {card.forgotten ? (
            <ForgottenBadge
              title={HARBOUR_TOOLTIPS.forgotten}
              data-testid={`harbour-card-forgotten-${card.venueId}`}
            >
              Forgotten
            </ForgottenBadge>
          ) : null}
          {card.nextFollowUpLabel ? (
            <FollowUpBadge
              data-follow-up={followUpStatus}
              data-testid={`harbour-card-follow-up-${card.venueId}`}
              title={card.nextFollowUpLabel}
            >
              {card.nextFollowUpLabel}
            </FollowUpBadge>
          ) : null}
          {otherSites.length > 0 ? (
            <SisterBadge
              title={otherSites
                .map(site => site.venueName)
                .filter(Boolean)
                .join(', ')}
              data-testid={`harbour-card-group-${card.venueId}`}
            >
              +{otherSites.length}{' '}
              {otherSites.length === 1 ? 'other site' : 'other sites'}
            </SisterBadge>
          ) : card.sisterVenueCount > 0 ? (
            <SisterBadge
              title={
                formatHarbourSisterTalkingPoint(card.sisterVenueCount) ||
                'Other site on the same owner account'
              }
            >
              +{card.sisterVenueCount}{' '}
              {card.sisterVenueCount === 1 ? 'other site' : 'other sites'}
            </SisterBadge>
          ) : null}
          {!phone ? (
            <NoPhoneBadge
              data-testid={`harbour-card-no-phone-badge-${card.venueId}`}
            >
              {HARBOUR_NO_PHONE_COPY}
            </NoPhoneBadge>
          ) : null}
        </Badges>
      </CardTop>
      {otherSites.length > 0 ? (
        <SisterSites data-testid={`harbour-card-sisters-${card.venueId}`}>
          {otherSites
            .map(site => site.venueName)
            .filter(Boolean)
            .join(' · ')}
        </SisterSites>
      ) : null}

      {phone ? (
        <ContactRow>
          <ContactName data-testid={`harbour-card-chef-${card.venueId}`}>
            {chefName || 'No named contact'}
          </ContactName>
          <ContactSep aria-hidden='true'>·</ContactSep>
          <PhoneNumber data-testid={`harbour-card-phone-${card.venueId}`}>
            {phone}
          </PhoneNumber>
          <CopyButton
            type='button'
            title={HARBOUR_TOOLTIPS.copyPhone}
            data-testid={`harbour-copy-phone-${card.venueId}`}
            onClick={handleCopyPhone}
          >
            Copy
          </CopyButton>
        </ContactRow>
      ) : (
        <>
          <ContactRow>
            <ContactName data-testid={`harbour-card-chef-${card.venueId}`}>
              {chefName || 'No named contact'}
            </ContactName>
          </ContactRow>
          <NoPhoneRow data-testid={`harbour-card-no-phone-${card.venueId}`}>
            {HARBOUR_NO_PHONE_COPY} — skip this call
          </NoPhoneRow>
        </>
      )}

      {csOwnerLabel ? (
        <CsOwner
          title={HARBOUR_TOOLTIPS.csOwner}
          data-testid={`harbour-card-owner-${card.venueId}`}
        >
          {csOwnerLabel}
        </CsOwner>
      ) : null}

      <Radar
        title={HARBOUR_TOOLTIPS.radar}
        data-testid={`harbour-card-radar-${card.venueId}`}
      >
        {radar.map(chip => (
          <RadarMetric
            key={chip.key}
            data-tone={chip.tone}
            data-testid={`harbour-radar-${card.venueId}-${chip.key}`}
          >
            <RadarLabel>{chip.label}</RadarLabel>
            <RadarValue>{chip.text}</RadarValue>
          </RadarMetric>
        ))}
      </Radar>

      {ladderCritical.length > 0 || ladderSecondary.length > 0 ? (
        <Ladder
          title={HARBOUR_TOOLTIPS.ladder}
          data-testid={`harbour-card-ladder-${card.venueId}`}
        >
          {ladderCritical.map(tick => (
            <LadderTick
              key={tick.key}
              data-tier={tick.tier}
              data-tone={tick.tone}
              data-testid={`harbour-ladder-${card.venueId}-${tick.key}`}
              title={`${tick.label}: ${tick.text}`}
            >
              {tick.label}: {tick.text}
            </LadderTick>
          ))}
          {ladderSecondary.length > 0 ? (
            <LadderSecondary
              data-testid={`harbour-card-ladder-secondary-${card.venueId}`}
            >
              {ladderSecondary.map(tick => (
                <LadderTick
                  key={tick.key}
                  data-tier={tick.tier}
                  data-tone={tick.tone}
                  data-testid={`harbour-ladder-${card.venueId}-${tick.key}`}
                  title={`${tick.label}: ${tick.text}`}
                >
                  {tick.label}: {tick.text}
                </LadderTick>
              ))}
            </LadderSecondary>
          ) : null}
        </Ladder>
      ) : null}

      {card.whyHere ? (
        <WhyHere data-testid={`harbour-card-why-${card.venueId}`}>
          {card.whyHere}
        </WhyHere>
      ) : null}

      {card.suggestedNextStep ? (
        <Suggested data-testid={`harbour-card-step-${card.venueId}`}>
          {card.suggestedNextStep}
        </Suggested>
      ) : null}

      {card.talkingPoints?.length > 0 ? (
        <TalkingList
          data-testid={`harbour-card-talking-${card.venueId}`}
          title={HARBOUR_TOOLTIPS.talkingPoints}
        >
          {card.talkingPoints.map(point => (
            <li key={point}>{point}</li>
          ))}
        </TalkingList>
      ) : null}

      {askSales || askProduct ? (
        <NudgeRow>
          {askSales ? (
            <AskSales
              type='button'
              title={HARBOUR_TOOLTIPS.askSales}
              data-testid={`harbour-ask-sales-${card.venueId}`}
              onClick={handleAskSales}
            >
              {askSales.label}
            </AskSales>
          ) : null}
          {askProduct ? (
            <AskProduct
              type='button'
              title={HARBOUR_TOOLTIPS.askProduct}
              data-testid={`harbour-ask-product-${card.venueId}`}
              onClick={handleAskProduct}
            >
              {askProduct.label}
            </AskProduct>
          ) : null}
        </NudgeRow>
      ) : null}

      <Actions onClick={event => event.stopPropagation()}>
        {canHarbourStartWork(card) ? (
          <StartButton
            type='button'
            title={HARBOUR_TOOLTIPS.startWork}
            disabled={startBusy}
            data-testid={`harbour-start-work-${card.venueId}`}
            onClick={event => onStartWork?.(event, card)}
          >
            {startBusy ? 'Starting…' : 'Start work'}
          </StartButton>
        ) : null}
        {phoneHref ? (
          <CallLink
            href={phoneHref}
            title={HARBOUR_TOOLTIPS.call}
            data-testid={`harbour-call-${card.venueId}`}
          >
            Call
          </CallLink>
        ) : null}
        {whatsAppChatHref ? (
          <WhatsAppChatLink
            href={whatsAppChatHref}
            title={HARBOUR_TOOLTIPS.whatsAppChat}
            data-testid={`harbour-whatsapp-chat-${card.venueId}`}
            target='_blank'
            rel='noopener noreferrer'
          >
            {HARBOUR_WHATSAPP_CHAT_ACTION}
          </WhatsAppChatLink>
        ) : null}
        {emails.length > 0 ? (
          <CopyEmailsButton
            type='button'
            title={HARBOUR_TOOLTIPS.copyEmails}
            data-emphasized={!phoneHref ? 'true' : 'false'}
            data-testid={`harbour-copy-emails-${card.venueId}`}
            onClick={handleCopyEmails}
          >
            {HARBOUR_COPY_EMAILS_ACTION}
          </CopyEmailsButton>
        ) : null}
      </Actions>
    </Card>
  )
}

const Card = styled.article`
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 16.5rem;
  max-width: 22rem;
  padding: 12px;
  background: #fff;
  border: 1px solid rgba(0, 0, 0, 0.08);
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  cursor: default;
  text-align: left;

  &:hover {
    border-color: rgba(0, 0, 0, 0.14);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.08);
  }

  &[data-no-phone='true'] {
    border-color: #e8c4c3;
    border-left: 4px solid #c45c5c;
    background: #fff6f5;
  }

  &[data-overdue='true'],
  &[data-forgotten='true'] {
    border-color: #e8c4c3;
    border-left: 4px solid #c45c5c;
    background: #fff6f5;
  }
`

const CardTop = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
`

const TitleBlock = styled.div`
  min-width: 0;
  flex: 1 1 auto;
`

const VenueName = styled.h4`
  margin: 0;
  font-size: 14px;
  font-weight: 600;
  line-height: 1.3;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Badges = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 4px;
`

const PlanBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #eef1f4;
  color: #555;

  &[data-plan='paid'] {
    background: #e8f3d8;
    color: #2d5a1e;
  }
`

const FollowUpBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #eef1f4;
  color: #555;

  &[data-follow-up='overdue'] {
    color: #7a2e2e;
    background: #f7e7e7;
  }

  &[data-follow-up='today'] {
    color: #7a5a1e;
    background: #f7f0e0;
  }
`

const ForgottenBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  color: #7a2e2e;
  background: #f7e7e7;
`

const SisterBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  background: #f4f8f6;
  color: #5b8a72;
`

const SisterSites = styled.p`
  margin: 4px 0 0;
  font-size: 12px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const NoPhoneBadge = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 700;
  color: #7a2e2e;
  background: #f7e7e7;
`

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 13px;
  line-height: 1.35;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const ContactName = styled.span`
  font-weight: 600;
`

const ContactSep = styled.span`
  color: ${({ theme }) => theme.colours.oliveGrey || '#888'};
  font-weight: 400;
`

const NoPhoneRow = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 8px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 700;
  color: #7a2e2e;
  background: #fdf6f6;
`

const PhoneNumber = styled.span`
  font-variant-numeric: tabular-nums;
`

const CopyButton = styled.button`
  border: 0;
  background: transparent;
  cursor: pointer;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const CsOwner = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colours.stormGrey || '#666'};
`

const Radar = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
`

const RadarMetric = styled.div`
  padding: 6px 8px;
  border-radius: 6px;
  background: #f6f7f8;

  &[data-tone='fresh'] {
    background: #e8f3d8;
    color: #2d5a1e;
  }

  &[data-tone='quiet'] {
    background: #f7f0e0;
    color: #7a5a1e;
  }

  &[data-tone='never'] {
    background: #f7e7e7;
    color: #7a2e2e;
    font-weight: 700;
  }
`

const RadarLabel = styled.div`
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
  opacity: 0.8;
`

const RadarValue = styled.div`
  font-size: 12px;
  font-weight: 700;
`

const Ladder = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px;
`

const LadderSecondary = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  opacity: 0.72;
`

const LadderTick = styled.span`
  padding: 1px 6px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  background: #f0f0f0;
  color: #666;

  &[data-tier='critical'] {
    font-size: 11px;
    font-weight: 700;
  }

  &[data-tier='secondary'] {
    font-size: 10px;
    font-weight: 500;
    background: #f4f4f4;
    color: #888;
  }

  &[data-tone='fresh'] {
    color: #2d5a1e;
    background: #e8f3d8;
  }

  &[data-tone='quiet'] {
    color: #7a5a1e;
    background: #f7f0e0;
  }

  &[data-tone='never'] {
    color: #7a2e2e;
    background: #f7e7e7;
    font-weight: 700;
  }

  &[data-tier='secondary'][data-tone='fresh'] {
    color: #5a7a4a;
    background: #eef3ea;
  }

  &[data-tier='secondary'][data-tone='quiet'] {
    color: #8a7a62;
    background: #f5f2eb;
  }

  &[data-tier='secondary'][data-tone='never'] {
    color: #9a6a6a;
    background: #f8f0f0;
    font-weight: 600;
  }
`

const WhyHere = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
`

const Suggested = styled.p`
  margin: 0;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#555'};
`

const TalkingList = styled.ul`
  margin: 0;
  padding-left: 1.1em;
  font-size: 12px;
  line-height: 1.4;
  color: ${({ theme }) => theme.colours.stormGrey || '#555'};
`

const NudgeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const AskSales = styled.button`
  align-self: flex-start;
  padding: 2px 8px;
  border: 1px solid #c5d4e4;
  border-radius: 999px;
  background: #f3f7fa;
  color: #3d6b8a;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
`

const AskProduct = styled.button`
  align-self: flex-start;
  padding: 2px 8px;
  border: 1px solid #e0c4c4;
  border-radius: 999px;
  background: #fdf3f3;
  color: #8a3d3d;
  font-size: 11px;
  font-weight: 700;
  cursor: pointer;
`

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 4px;
`

const actionControlStyles = css`
  flex: 1 1 7rem;
  min-width: 5.75rem;
  padding: 6px 10px;
  font-weight: 600;
  text-align: center;
  white-space: nowrap;
`

const StartButton = styled.button`
  ${actionControlStyles}
  border: 1px solid #d7e3dc;
  border-radius: 6px;
  background: #f4f8f6;
  color: #2d5a1e;
  cursor: pointer;

  &:disabled {
    cursor: wait;
    opacity: 0.7;
  }
`

const CallLink = styled.a`
  ${actionControlStyles}
  border-radius: 6px;
  background: #5b8a72;
  color: #fff;
  text-decoration: none;
  cursor: pointer;

  &[data-disabled='true'] {
    background: #e6e8eb;
    color: #888;
    pointer-events: none;
  }
`

const WhatsAppChatLink = styled.a`
  ${actionControlStyles}
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background: #fff;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  text-decoration: none;
  cursor: pointer;
`

const CopyEmailsButton = styled.button`
  ${actionControlStyles}
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 6px;
  background: #fff;
  color: ${({ theme }) => theme.colours.broccoliGreen || '#1a1a1a'};
  cursor: pointer;

  &[data-emphasized='true'] {
    border: 0;
    background: #5b8a72;
    color: #fff;
  }
`

export default HarbourCard
